/**
 * Pre-attach file validation. Pure function — no React, no admin
 * context — so it's trivially unit-testable and reusable.
 *
 * Validates each picked file against: allowed extensions, max size,
 * and cumulative file count (alreadyAttached + accepted so far must
 * stay <= maxFiles). Returns the accepted files and per-file
 * rejection records suitable for both inline UI display and dashboard
 * telemetry.
 */

import { UNSUPPORTED_IMAGE_EXTENSIONS } from './constants';

export type RejectionCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_many_files';

export interface FileRejection {
  file: File;
  code: RejectionCode;
  reason: string;
}

export interface ValidationLimits {
  maxFileSizeMB: number;
  maxFiles: number;
  allowedExtensions: string[];
}

export interface ValidationOptions {
  alreadyAttached: number;
  limits: ValidationLimits;
}

export interface ValidationResult {
  valid: File[];
  rejections: FileRejection[];
}

function extensionOf(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx).toLowerCase();
}

export function validatePickedFiles(
  picked: File[],
  { alreadyAttached, limits }: ValidationOptions,
): ValidationResult {
  const valid: File[] = [];
  const rejections: FileRejection[] = [];
  const allowedLower = limits.allowedExtensions.map((e) => e.toLowerCase());
  const maxBytes = limits.maxFileSizeMB * 1024 * 1024;

  for (const file of picked) {
    const ext = extensionOf(file.name);
    if (!allowedLower.includes(ext)) {
      const isKnownUnsupportedImage = (
        UNSUPPORTED_IMAGE_EXTENSIONS as readonly string[]
      ).includes(ext);
      rejections.push({
        file,
        code: 'unsupported_type',
        reason: isKnownUnsupportedImage
          ? `${ext} images aren't supported — convert to JPEG or PNG`
          : `unsupported type (${ext || 'no extension'})`,
      });
      continue;
    }
    if (file.size > maxBytes) {
      rejections.push({
        file,
        code: 'too_large',
        reason: `over ${limits.maxFileSizeMB} MB`,
      });
      continue;
    }
    if (alreadyAttached + valid.length >= limits.maxFiles) {
      rejections.push({
        file,
        code: 'too_many_files',
        reason: `max ${limits.maxFiles} files per message`,
      });
      continue;
    }
    valid.push(file);
  }

  return { valid, rejections };
}
